import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { Controller, FieldErrors, useForm } from 'react-hook-form'
import {
  Keyboard,
  KeyboardAvoidingView,
  View,
} from 'react-native'
import { Portal } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'
import z from 'zod'

import FormFieldDate from '@/components/form/FormFieldDate'
import FormFieldSelect from '@/components/form/FormFieldSelect'
import FormFieldText from '@/components/form/FormFieldText'
import TextInput from '@/components/form/TextInput'
import FormFieldVolunteer from '@/components/fosters/cards/formFields/FormFieldVolunteer'
import UniButton from '@/components/unistyles/UniButton'
import UniHelperText from '@/components/unistyles/UniHelperText'
import UniModal from '@/components/unistyles/UniModal'
import PaymentMethod from '@/enums/paymentMethod'
import FosterService from '@/services/fosterService'
import formatPhoneNumber from '@/utils/formatPhoneNumber'
import trpc from '@/utils/trpc'

// Phone number validation regex - allows formats like: (123) 456-7890, 123-456-7890, 1234567890
const phoneRegex =
  /^(\+1|1)?[-. ]?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/

const adoptionSchema = z.object({
  adoptedByName: z.string(),
  adoptedByEmail: z
    .string()
    .min(1, { message: 'Email is required' })
    .trim()
    .toLowerCase()
    .email({ message: 'Invalid Email' }),
  adoptedByPhone: z
    .string()
    .min(1, { message: 'Phone number is required' })
    .regex(phoneRegex, { message: 'Invalid phone number format' }),
  adoptedName: z.string().nullable().optional(),
  adoptionDate: z.date(),
  paymentMethod: z.nativeEnum(PaymentMethod),
  returnDate: z.date().nullable().optional(),
  returnReason: z.string().nullable().optional(),
  userId: z.string().uuid(),
})

export type AdoptionRecordForm = z.infer<typeof adoptionSchema>

interface Props {
  adoption?: {
    id: string
    adoptedByName: string | null
    adoptedByEmail: string | null
    adoptedByPhone: string | null
    adoptedName: string | null
    adoptionDate: Date | null
    paymentMethod: PaymentMethod | null
    returnDate: Date | null
    returnReason: string | null
    userId: string
  }
  open: boolean
  onClose: () => void
  mutation:
    | ReturnType<typeof trpc.foster.adoptions.create.useMutation>
    | ReturnType<typeof trpc.foster.adoptions.update.useMutation>
  fosterId: string
}

const AdoptionForm = ({
  adoption,
  open,
  onClose,
  mutation,
  fosterId,
}: Props) => {
  const {
    foster,
    refreshFoster,
    isRefetching: isFosterRefetching,
  } = FosterService.useFoster(fosterId)

  const defaultValues = useMemo(
    () => ({
      adoptedByName: adoption?.adoptedByName || '',
      adoptedByEmail: adoption?.adoptedByEmail || '',
      adoptedByPhone: adoption?.adoptedByPhone
        ? formatPhoneNumber(adoption.adoptedByPhone)
        : '',
      adoptedName: adoption?.adoptedName || null,
      adoptionDate: adoption?.adoptionDate || new Date(),
      userId: adoption?.userId || foster?.user.id,
      paymentMethod: adoption?.paymentMethod || PaymentMethod.Cash,
      returnDate: adoption?.returnDate || null,
      returnReason: adoption?.returnReason || '',
    }),
    [adoption, foster?.user.id]
  )

  const { control, formState, handleSubmit, reset } =
    useForm<AdoptionRecordForm>({
      mode: 'onBlur',
      resolver: zodResolver(adoptionSchema),
      defaultValues,
    })

  useEffect(() => {
    reset(
      {
        ...defaultValues,
      },
      {
        keepDirty: true,
        keepDirtyValues: true,
      }
    )
  }, [defaultValues, reset, foster?.user.id])

  const submit = async () => {
    Keyboard.dismiss()
    await new Promise<void>((resolve) => setTimeout(resolve, 100))

    await handleSubmit(async (data) => {
      mutation.mutateAsync(
        {
          fosterId,
          organizationId: foster?.organizationId || '',
          id: adoption?.id || '',
          data: {
            ...data,
          },
        },
        {
          onSuccess: async () => {
            await refreshFoster()
            reset()
            onClose()
          },
        }
      )
    })()
  }

  return (
    <>
      {open && (
        <Portal>
          <KeyboardAvoidingView
            behavior="height"
            enabled={open ? true : false}
            style={{
              height: open ? '100%' : 'auto',
            }}
          >
            <UniModal
              contentContainerStyle={styles.root}
              onDismiss={onClose}
              visible={open}
            >
              <View style={styles.containerColumn}>
                <FormFieldText
                  control={control}
                  errors={formState.errors}
                  fieldName="adoptedByName"
                  label="Adopted By"
                  required
                  style={styles.input}
                />
                <FormFieldText
                  autoCapitalize="none"
                  control={control}
                  errors={formState.errors}
                  fieldName="adoptedByEmail"
                  label="Adopter's Email"
                  required
                  style={styles.input}
                />
                <Controller
                  control={control}
                  name="adoptedByPhone"
                  render={({ field: { onChange, value } }) => (
                    <View style={styles.input}>
                      <TextInput
                        accessibilityHint="Enter the adopter's phone number. It will be automatically formatted as you type."
                        accessibilityLabel="Adopter's phone number input"
                        error={!!formState.errors.adoptedByPhone}
                        keyboardType="phone-pad"
                        label="Adopter's Phone"
                        onChangeText={(text) => {
                          const formatted = formatPhoneNumber(text)
                          onChange(formatted)
                        }}
                        testID="adoptedByPhone-textInput"
                        value={value}
                      />
                      {formState.errors.adoptedByPhone && (
                        <UniHelperText type="error" visible={true}>
                          {formState.errors.adoptedByPhone.message}
                        </UniHelperText>
                      )}
                    </View>
                  )}
                />
              </View>
              <View style={styles.containerRow}>
                <FormFieldDate
                  control={control}
                  errors={formState.errors}
                  fieldName="adoptionDate"
                  label="Adoption Date"
                  required
                  style={styles.input}
                />
                <FormFieldSelect
                  control={control}
                  data={Object.values(PaymentMethod).map((value) => ({
                    label: value,
                    value,
                  }))}
                  errors={formState.errors as FieldErrors}
                  fieldName="paymentMethod"
                  label="Payment Method"
                  required
                  style={{ flex: 1 }}
                />
              </View>
              <View style={styles.containerColumn}>
                <FormFieldText
                  control={control}
                  errors={formState.errors}
                  fieldName="adoptedName"
                  label="Pet's Adopted Name (if different)"
                  required
                  style={styles.input}
                />
                {adoption?.id && (
                  <FormFieldVolunteer
                    control={control}
                    formState={formState}
                    label="Fostered By"
                    style={styles.input}
                  />
                )}
              </View>
              {defaultValues?.returnDate && (
                <View style={styles.containerColumn}>
                  <FormFieldDate
                    control={control}
                    errors={formState.errors}
                    fieldName="returnDate"
                    label="Return Date"
                    required={false}
                    style={styles.input}
                  />
                  <FormFieldText
                    control={control}
                    errors={formState.errors}
                    fieldName="returnReason"
                    label="Reason for Return"
                    required={false}
                    style={styles.input}
                  />
                </View>
              )}
              <UniButton
                disabled={mutation.isPending || isFosterRefetching}
                loading={mutation.isPending || isFosterRefetching}
                mode="contained"
                onPress={submit}
              >
                {adoption ? 'Update' : 'Save'}
              </UniButton>
            </UniModal>
          </KeyboardAvoidingView>
        </Portal>
      )}
    </>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    containerColumn: {
      flexDirection: {
        xs: 'column',
        md: 'row',
      },
      gap: theme.tokens.spacing[3],
    },
    containerRow: {
      flexDirection: 'row',
      gap: theme.tokens.spacing[3],
    },
    input: {
      flex: {
        md: 1,
      },
    },
    root: {
      alignSelf: 'center',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.tokens.containerBorderRadius,
      gap: theme.tokens.spacing[3],
      width: {
        xs: '95%',
        lg: '65%',
        xl: '50%',
        xxl: '36%',
      },
      padding: theme.tokens.spacing[6],
    },
  }
})

export default AdoptionForm
